import { useState } from 'react';

export default function Contact() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');

    const form = e.currentTarget;
    const formData = new FormData(form);
    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      params.append(key, value.toString());
    });

    try {
      const response = await fetch('https://readdy.ai/api/form/d7o4vm62b60ajre49b1g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      if (response.ok) {
        setFormStatus('success');
        form.reset();
      } else {
        setFormStatus('error');
      }
    } catch {
      setFormStatus('error');
    }

    setTimeout(() => setFormStatus('idle'), 3000);
  };

  return (
    <main>
      {/* Page Header */}
      <section className="relative pt-24 md:pt-32 pb-12 md:pb-16 bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a] overflow-hidden">
        {/* 网格纹理 */}
        <div
          className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.3) 1px, transparent 1px)',
            backgroundSize: '40px 40px',
          }}
        />
        {/* 右上光晕 */}
        <div
          className="absolute -top-20 -right-20 w-[500px] h-[500px] rounded-full opacity-[0.2] pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(0,174,239,0.4) 0%, transparent 70%)' }}
        />
        {/* 左下光晕 */}
        <div
          className="absolute -bottom-32 -left-32 w-[400px] h-[400px] rounded-full opacity-[0.15] pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(0,174,239,0.3) 0%, transparent 70%)' }}
        />
        {/* 动态浮动元素 */}
        <div className="absolute top-24 right-[22%] w-2.5 h-2.5 bg-accent/40 rounded-full animate-pulse pointer-events-none" />
        <div className="absolute bottom-24 left-[15%] w-2 h-2 bg-white/30 rounded-full animate-pulse pointer-events-none" style={{ animationDelay: '0.8s' }} />
        <div className="absolute top-40 left-[35%] w-1.5 h-1.5 bg-accent/30 rounded-full animate-pulse pointer-events-none" style={{ animationDelay: '1.6s' }} />
        <div className="absolute top-32 right-[40%] w-1 h-1 bg-white/20 rounded-full animate-pulse pointer-events-none" style={{ animationDelay: '2.2s' }} />
        {/* 装饰弧线 */}
        <div className="absolute top-[20%] right-[5%] w-[220px] h-[220px] rounded-full border border-[#00AEEF]/10 pointer-events-none" />
        <div className="absolute top-[25%] right-[8%] w-[160px] h-[160px] rounded-full border border-[#00AEEF]/10 pointer-events-none" />
        {/* 装饰线条 */}
        <div className="absolute top-[10%] right-[12%] w-px h-[300px] bg-gradient-to-b from-transparent via-accent/15 to-transparent pointer-events-none" />
        <div className="absolute bottom-[15%] left-[8%] w-[120px] h-px bg-gradient-to-r from-transparent via-accent/10 to-transparent pointer-events-none" />

        <div className="relative z-10 w-full px-4 md:px-8 lg:px-12">
          <div className="max-w-3xl">
            <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
              预约专属客服
            </h1>
            <p className="text-sm md:text-base text-white/60 leading-relaxed">
              30分钟，找到您营销数字化的最优切入点
            </p>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-16 md:py-24 bg-white">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-16">
            {/* Left: Info */}
            <div className="lg:w-2/5">
              <h2 className="text-xl md:text-2xl font-bold text-primary mb-4">
                由资深顾问一对一沟通
              </h2>
              <p className="text-sm md:text-base text-text-secondary leading-relaxed mb-8">
                帮助您：快速识别核心痛点 · 了解适合阶段的解决方案 · 获得初步ROI预估
              </p>

              <div className="space-y-5">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-accent/10 rounded-lg shrink-0">
                    <i className="ri-map-pin-line text-accent" />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-primary mb-1">深圳总部</h4>
                    <p className="text-sm text-text-secondary">深圳市宝安区西乡街道劳动社区前海科兴科学园5号楼702</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-accent/10 rounded-lg shrink-0">
                    <i className="ri-map-pin-line text-accent" />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-primary mb-1">成都分公司</h4>
                    <p className="text-sm text-text-secondary">四川省成都市高新区天府大道中段500号1栋4507</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-accent/10 rounded-lg shrink-0">
                    <i className="ri-phone-line text-accent" />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-primary mb-1">市场 & 媒体合作</h4>
                    <p className="text-sm text-text-secondary">柳雪琴 · 18688924109</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-accent/10 rounded-lg shrink-0">
                    <i className="ri-mail-line text-accent" />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-primary mb-1">商务邮箱</h4>
                    <p className="text-sm text-text-secondary">liuxueqin@ronghuidt.com</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-accent/10 rounded-lg shrink-0">
                    <i className="ri-wechat-line text-accent" />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-primary mb-1">企业微信</h4>
                    <p className="text-sm text-text-secondary">扫码添加专属顾问，工作日24小时内响应</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Form */}
            <div className="lg:w-3/5">
              <div className="bg-surface rounded-xl p-6 md:p-8 border border-gray-100">
                <h3 className="text-lg md:text-xl font-bold text-primary mb-6">
                  填写预约信息
                </h3>

                <form data-readdy-form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-primary mb-1.5">
                        姓名 <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        required
                        className="w-full h-12 px-4 bg-white border border-gray-200 rounded-lg text-sm text-primary placeholder:text-text-muted focus:outline-none focus:border-accent transition-colors"
                        placeholder="您的姓名"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-primary mb-1.5">
                        公司名称 <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="company"
                        required
                        className="w-full h-12 px-4 bg-white border border-gray-200 rounded-lg text-sm text-primary placeholder:text-text-muted focus:outline-none focus:border-accent transition-colors"
                        placeholder="公司名称"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-primary mb-1.5">
                        职位
                      </label>
                      <input
                        type="text"
                        name="position"
                        className="w-full h-12 px-4 bg-white border border-gray-200 rounded-lg text-sm text-primary placeholder:text-text-muted focus:outline-none focus:border-accent transition-colors"
                        placeholder="您的职位"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-primary mb-1.5">
                        手机号 <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        required
                        className="w-full h-12 px-4 bg-white border border-gray-200 rounded-lg text-sm text-primary placeholder:text-text-muted focus:outline-none focus:border-accent transition-colors"
                        placeholder="手机号"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-primary mb-1.5">
                      主要痛点（选填）
                    </label>
                    <textarea
                      name="pain_point"
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg text-sm text-primary placeholder:text-text-muted focus:outline-none focus:border-accent transition-colors resize-none"
                      placeholder="请简要描述您目前面临的主要营销数字化痛点..."
                    />
                    <p className="text-xs text-text-muted mt-1">最多500字</p>
                  </div>

                  <button
                    type="submit"
                    disabled={formStatus === 'submitting'}
                    className="w-full h-12 bg-accent text-white font-semibold rounded-lg hover:bg-accent-dark transition-colors disabled:opacity-70 whitespace-nowrap"
                  >
                    {formStatus === 'submitting'
                      ? '提交中...'
                      : formStatus === 'success'
                        ? '预约成功！'
                        : formStatus === 'error'
                          ? '提交失败，请重试'
                          : '提交预约'}
                  </button>
                </form>

                <p className="text-xs text-text-muted text-center mt-4">
                  提交后顾问将在24小时内与您联系
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
